import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Send } from "lucide-react";

export default function Messages() {
  const { other } = useParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [otherUser, setOtherUser] = useState(null);
  const bottomRef = useRef(null);
  const navigate = useNavigate();

  const loadConv = async () => { const { data } = await api.get("/messages/conversations"); setConversations(data); };
  useEffect(() => { loadConv(); api.get("/users").then(r => setUsers(r.data.filter(u => u.id !== user?.id))); }, [user?.id]);

  useEffect(() => {
    if (!other) { setThread([]); setOtherUser(null); return; }
    api.get(`/users/${other}`).then(r => setOtherUser(r.data));
    api.get(`/messages/${other}`).then(r => setThread(r.data));
  }, [other]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !other) return;
    await api.post("/messages", { recipient_id: other, content: text });
    setText("");
    const { data } = await api.get(`/messages/${other}`);
    setThread(data);
    loadConv();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="messages-page">
      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-6 h-[75vh]">
        <aside className="card-flat overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b">
            <div className="label-eyebrow">Conversations</div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 && <div className="p-6 text-sm text-slate-500">No conversations yet. Start one →</div>}
            {conversations.map(c => (
              <button key={c.user.id} onClick={() => navigate(`/messages/${c.user.id}`)} className={`w-full text-left px-4 py-3 border-b hover:bg-[#FDFBF7] flex gap-3 items-start ${other === c.user.id ? "bg-[#FDFBF7]" : ""}`} data-testid={`conv-${c.user.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#002855] text-[#C5A059] flex items-center justify-center text-sm font-semibold">{c.user.name?.[0]}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0A192F] truncate">{c.user.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.last_message}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="border-t px-4 py-3">
            <div className="label-eyebrow">Start a message</div>
            <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
              {users.slice(0, 12).map(u => (
                <button key={u.id} onClick={() => navigate(`/messages/${u.id}`)} className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-[#FDFBF7] text-slate-700">{u.name} <span className="text-xs text-slate-400">· {u.agency || "—"}</span></button>
              ))}
            </div>
          </div>
        </aside>

        <section className="card-flat overflow-hidden flex flex-col">
          {otherUser ? (
            <>
              <div className="px-5 py-3 border-b flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#002855] text-[#C5A059] flex items-center justify-center text-sm font-semibold">{otherUser.name?.[0]}</div>
                <div>
                  <Link to={`/u/${otherUser.id}`} className="text-sm font-semibold text-[#0A192F] hover:text-[#002855]">{otherUser.name}</Link>
                  <div className="text-xs text-slate-500">{otherUser.agency} · Cohort {otherUser.cohort_year || "—"}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {thread.map(m => (
                  <div key={m.id} className={`max-w-[75%] ${m.mine ? "ml-auto" : ""}`}>
                    <div className={`px-4 py-2.5 rounded-md text-sm ${m.mine ? "bg-[#002855] text-white" : "bg-[#FDFBF7] border border-slate-200 text-[#0A192F]"}`} data-testid={`msg-${m.id}`}>{m.content}</div>
                    <div className={`text-[10px] mt-1 text-slate-400 ${m.mine ? "text-right" : ""}`}>{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="border-t px-4 py-3 flex gap-2">
                <input value={text} onChange={e => setText(e.target.value)} placeholder="Write a message…" className="flex-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002855]/20" data-testid="message-input" />
                <Button type="submit" className="bg-[#002855] hover:bg-[#0A192F] text-white h-10 rounded-md" data-testid="message-send-button"><Send className="w-4 h-4" /></Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Select a conversation</div>
          )}
        </section>
      </div>
    </div>
  );
}
